import { authFetch } from "utils/authFetch";
import type {
  RequestReroute,
  RerouteCircuitResult,
  RerouteResult,
} from "./types";

const API_URL =
  process.env.NODE_ENV === "production"
    ? ""
    : (process.env.REACT_APP_BACKEND_URL ?? "http://localhost:5050");

/** The server's own shape. Named apart so a rename there is a compile error here. */
interface RoutedCircuitResponse {
  ref: string;
  paths: Array<{ elementId: string; waypoints: Array<{ x: number; y: number }> }>;
  billedM: number;
  onTrayM: number;
  routingMode: "tray" | "point_to_point";
  trayTypesRidden: string[];
  trayFallbackReason: string;
  wallCrossings?: number;
}

/**
 * Asks the app's own router where this run's cable goes.
 *
 * The trays are NOT sent: the server reads them from `quantity_surveyor_routes`,
 * which is the whole point of moving routing here — the estimator keeps drawing
 * and renaming them, and any copy the client carried would be one edit behind
 * exactly the way the agent's import was.
 *
 * A failure answers "nothing changed" rather than throwing. The caller writes
 * nothing on an empty path list, so a dead server leaves the drawing and the
 * metres exactly as they were — which is the honest outcome, and the same one
 * the stub gives.
 */
export const requestRouting: RequestReroute = async (request) => {
  const nothingChanged: RerouteResult = {
    circuits: request.circuits.map(
      (circuit): RerouteCircuitResult => ({
        ref: circuit.ref,
        paths: [],
        billedM: 0,
        onTrayM: 0,
        routingMode: "point_to_point",
        trayTypesRidden: [],
        trayFallbackReason: "",
        wallCrossings: 0,
      }),
    ),
  };

  try {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-cable-routing/circuits`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          floorPlanId: request.floorPlanId,
          noTraySurchargePct: request.noTraySurchargePct,
          trayTypes: request.trayTypes,
          circuits: request.circuits.map((circuit) => ({
            ref: circuit.ref,
            trayType: circuit.trayType,
            source: circuit.source,
            destinations: circuit.destinations,
          })),
        }),
      },
    );
    if (!response.ok) return nothingChanged;
    const body = (await response.json()) as { circuits?: RoutedCircuitResponse[] };
    if (!Array.isArray(body.circuits)) return nothingChanged;
    return { circuits: body.circuits };
  } catch {
    return nothingChanged;
  }
};
